import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export const EXAMPLE_SENTENCE_SORT_STORAGE_KEY = 'memrise_example_sentence_sort';

export const EXAMPLE_SENTENCE_SORT_FIELDS = ['difficulty', 'length', 'source'] as const;
export const EXAMPLE_SENTENCE_SORT_DIRECTIONS = ['asc', 'desc'] as const;

export type ExampleSentenceSortField = (typeof EXAMPLE_SENTENCE_SORT_FIELDS)[number];
export type ExampleSentenceSortDirection = (typeof EXAMPLE_SENTENCE_SORT_DIRECTIONS)[number];

export type ExampleSentenceSortPreference = {
  field: ExampleSentenceSortField;
  direction: ExampleSentenceSortDirection;
};

export const DEFAULT_EXAMPLE_SENTENCE_SORT: ExampleSentenceSortPreference = {
  field: 'difficulty',
  direction: 'asc',
};

const isSortField = (value: unknown): value is ExampleSentenceSortField =>
  EXAMPLE_SENTENCE_SORT_FIELDS.some(field => field === value);

const isSortDirection = (value: unknown): value is ExampleSentenceSortDirection =>
  EXAMPLE_SENTENCE_SORT_DIRECTIONS.some(direction => direction === value);

const parsePreference = (raw: string | null): ExampleSentenceSortPreference => {
  if (!raw) {
    return DEFAULT_EXAMPLE_SENTENCE_SORT;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<ExampleSentenceSortPreference>;
    if (isSortField(parsed.field) && isSortDirection(parsed.direction)) {
      return { field: parsed.field, direction: parsed.direction };
    }
  } catch {
    return DEFAULT_EXAMPLE_SENTENCE_SORT;
  }

  return DEFAULT_EXAMPLE_SENTENCE_SORT;
};

export const getExampleSentenceSort = (): ExampleSentenceSortPreference => {
  /* istanbul ignore next: SSR guard */
  if (typeof window === 'undefined') return DEFAULT_EXAMPLE_SENTENCE_SORT;

  return parsePreference(getLocalStorageEntry(EXAMPLE_SENTENCE_SORT_STORAGE_KEY));
};

export const setExampleSentenceSort = (preference: ExampleSentenceSortPreference): void => {
  /* istanbul ignore next: SSR guard */
  if (typeof window === 'undefined') return;

  setLocalStorageEntry(EXAMPLE_SENTENCE_SORT_STORAGE_KEY, JSON.stringify(preference));
};
